import { Link } from 'react-router-dom';

import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';

import {
  StyledGradientCard,
  StyledText,
} from '@webclient/components/settings/components/user-account-action-card/styles';

import useSecureAccountModal from '@webclient/hooks/useSecureAccountModal';

import Lightbulb from '~icons/knowz-iconify/lightbulb';
import StarIcon from '~icons/knowz-iconify/star';

import { Text } from '@knowz/knowz-ui';
import { useTranslate } from '@tolgee/react';
import paths from '@webclient/router/paths';

type Props = {
  accountType:
    | 'pack'
    | 'geniusSearch'
    | 'geniusTeams'
    | 'visitor'
    | 'free'
    | null;
  type: 'invitedTo' | 'yourKnowledgeBase' | 'knowledgeBaseDropdown' | 'search';
  onClose?: (event: React.SyntheticEvent) => void;
};

export default function UserAccountActionCard({
  accountType,
  type,
  onClose,
}: Props) {
  const { t } = useTranslate();
  const { openSecureAccount } = useSecureAccountModal();

  if (accountType === 'free' && type === 'search') {
    return (
      <StyledGradientCard sx={{ mt: -1.5 }}>
        <Stack
          direction="row"
          gap={2}
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Lightbulb fontSize={20} />

          <Text
            variant="textMd"
            weight="semibold"
          >
            {t(
              'layout.main.sections.header.settings.cta.search.free.title',
              'Upgrade for more',
            )}
          </Text>
        </Stack>

        <Text variant="textSm">
          {t(
            'layout.main.sections.header.settings.cta.search.free.content',
            'To make of the most of Knowz grab a credit pack or pick a plan to get access to advanced features and more.',
          )}
        </Text>

        <Button
          component={Link}
          to={paths.upgradeAccount.pathname}
          startIcon={<StarIcon />}
          color="secondary"
          fullWidth
          size="medium"
          variant="contained"
          sx={{ mt: 4 }}
          onClick={onClose}
        >
          {t(
            'layout.main.sections.header.settings.cta.search.free.button',
            'Upgrade Today',
          )}
        </Button>
      </StyledGradientCard>
    );
  }

  if (accountType === 'visitor' && type === 'yourKnowledgeBase') {
    return (
      <StyledGradientCard>
        <Stack
          direction="row"
          gap={2}
        >
          <StarIcon />
          <Text
            variant="textMd"
            weight="semibold"
          >
            {t(
              'layout.main.sections.header.settings.cta.yourKnowledgeBase.visitor.title',
              'Login or Sign up now',
            )}
          </Text>
        </Stack>
        <StyledText variant="textSm">
          {t(
            'layout.main.sections.header.settings.cta.yourKnowledgeBase.visitor.content',
            'Think of Knowledge Bases as memory silos, each training a unique version of Knowz. Invite others to your Knowledge Base & access advanced platform features by logging in or signing up now.',
          )}
        </StyledText>
        <Button
          variant="contained"
          fullWidth
          onClick={openSecureAccount}
        >
          {t('layout.auth.desktop.modal.steps.setupPassword.form.button', {
            defaultValue: 'Continue',
          })}
        </Button>
      </StyledGradientCard>
    );
  }

  if (accountType === 'visitor' && type === 'invitedTo') {
    return (
      <StyledGradientCard>
        <Stack>
          <Stack
            direction="row"
            gap={2}
          >
            <StarIcon />
            <Text
              variant="textMd"
              weight="semibold"
            >
              {t(
                'layout.main.sections.header.settings.cta.invitedTo.visitor.title',
                'Login or Sign up now',
              )}
            </Text>
          </Stack>
          <Lightbulb />
        </Stack>
        <Text variant="textSm">
          {t(
            'layout.main.sections.header.settings.cta.invitedTo.visitor.content',
            'Customize your profile, invite others to your Knowledge Base & access advanced platform features by logging in or signing up now.',
          )}
        </Text>
        <Button
          variant="contained"
          fullWidth
          onClick={openSecureAccount}
        >
          {t('layout.auth.desktop.modal.steps.setupPassword.form.button', {
            defaultValue: 'Continue',
          })}
        </Button>
      </StyledGradientCard>
    );
  }

  if (accountType === 'visitor' && type === 'knowledgeBaseDropdown') {
    return (
      <StyledGradientCard>
        <Text
          variant="textMd"
          weight="semibold"
        >
          {t(
            'layout.main.sections.header.settings.cta.knowledgeBaseDropdown.visitor.title',
            'Need More Knowledge Bases?',
          )}
        </Text>
        <Stack>
          <Text
            variant="textXs"
            weight="semibold"
          >
            {t(
              'layout.main.sections.header.settings.cta.knowledgeBaseDropdown.visitor.subtitle',
              'Credits power Knowz, upgrade now for more',
            )}
          </Text>
          <Text variant="textSm">
            {t(
              'layout.main.sections.header.settings.cta.knowledgeBaseDropdown.visitor.content',
              'Login or sign up now and pick a plan to create up to 5 Knowledge Bases and invite up to 100 users to each one!',
            )}
          </Text>
        </Stack>
      </StyledGradientCard>
    );
  }
}
